import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Layout from './pages/layout';
import { setUser } from './store/reducers/userReducer';
import Dashboard from './pages/dashboard';
import MyRecruiters from './pages/recruit-information/myRecruits';
import AllRecruiters from './pages/recruit-information/allRecruits';
import RecruitInformation from './pages/recruit-information';
import { route } from './constants';
import AddRecruitDetails from './pages/recruit-information/addRecruitDetails';
import EditOrViewRecruitDetails from './pages/recruit-information/editOrViewRecruitDetails';
import RecruitPortal from './pages/recruit-portal';
import PageNotFound from './pages/PageNotFound';

import { Roles } from './constants';
import { getLoggedInUserManagerId } from './services/recruitsService';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { UserRoles } from './models/userRoles.model';
import { getToken, isTokenExpired } from './services/api/axios';
import UnknownRoute from './pages/UnknownRoute';
import Container from './pages/container';

const AppRoutes = () => {
	const { instance } = useMsal();
	const [canAddRecruitDetails, setCanAddRecruitDetails] = useState(false);
	const userInfo = useSelector((state: RootState) => state.user);
	const [userAuthenticated, setUserAuthenticated] = useState(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	useEffect(() => {
		if (instance.getActiveAccount()) {
			const handle = async () => {
				//save login token in session storage
				const accessToken = sessionStorage.getItem(
					(process.env.REACT_APP_SCOPES as string) || '',
				);
				if (accessToken && isTokenExpired(accessToken)) {
					const updatedToken = await getToken();
					sessionStorage.removeItem(
						process.env.REACT_APP_SCOPES as string,
					);
					sessionStorage.setItem(
						(process.env.REACT_APP_SCOPES as string) || '',
						updatedToken || '',
					);
				} else {
					const updatedToken = await getToken();
					sessionStorage.setItem(
						(process.env.REACT_APP_SCOPES as string) || '',
						updatedToken || '',
					);
				}
				const userRoles = Object.entries(Roles);
				const tokenRoles = instance.getActiveAccount()?.idTokenClaims
					?.roles || [''];
				const hasAdminAppNoAccess = tokenRoles?.includes(
					Roles.adminAppNoAccess,
				);
				const hasAdminAppAccess = tokenRoles?.includes(
					Roles.adminAppAccess,
				);
				const doesNotHaveAccess =
					hasAdminAppNoAccess || !hasAdminAppAccess;
				if (doesNotHaveAccess) {
					setLoading(true);
					navigate('PageNotFound');
					return;
				}
				let roles: UserRoles = userInfo.roles;
				instance
					.getActiveAccount()
					?.idTokenClaims?.roles?.forEach((e: string) => {
						const keyOfUserRole = userRoles.find((userRole) => {
							const [policyKey, policyValue] = userRole;
							if (policyValue === e) return e;
						})?.[0] as string;
						if (keyOfUserRole) {
							roles = { ...roles, [keyOfUserRole]: true };
						}
					});
				const managerId = await getLoggedInUserManagerId({
					userEmail: instance.getActiveAccount()?.username as string,
				});
				const canAddRecruitDetails = instance
					.getActiveAccount()
					?.idTokenClaims?.roles?.find(
						(e) => e === Roles.adminAppAddRecruit,
					);
				setCanAddRecruitDetails(!!canAddRecruitDetails);
				setUserAuthenticated(true);
				dispatch(
					setUser({
						user_email: instance.getActiveAccount()
							?.username as string,
						user_name: instance.getActiveAccount()?.name,
						role: instance.getActiveAccount()?.idTokenClaims?.roles,
						id: managerId?.value,
						roles: roles,
					}),
				);
				if (
					location.pathname.length < 2 ||
					location.pathname === '/PageNotFound'
				) {
					navigate('/dashboard');
				}
			};
			handle();
		}
	}, [instance]);

	return (
		<Routes>
			{userAuthenticated ? (
				<Route path="/" element={<Layout />}>
					<Route element={<Container />}>
						<Route
							path={`${route.dashboard}`}
							element={<Dashboard />}
						></Route>
						<Route
							path={`${route.recruitPortal}`}
							element={<RecruitPortal />}
						></Route>
						<Route
							path={`/${route.recruitInformation}/*`}
							element={<RecruitInformation />}
						>
							<Route
								path={`${route.myRecruits}`}
								element={<MyRecruiters />}
							></Route>
							<Route
								path={`${route.allRecruits}`}
								element={<AllRecruiters />}
							></Route>
							{canAddRecruitDetails ? (
								<Route
									path={`${route.addDetails}`}
									element={<AddRecruitDetails />}
								></Route>
							) : null}
							<Route
								path={`${route.details}`}
								element={<EditOrViewRecruitDetails />}
							></Route>
							<Route path="*" element={<UnknownRoute />}></Route>
						</Route>
					</Route>
					<Route path="*" element={<UnknownRoute />}></Route>
					<Route
						path="PageNotFound"
						element={<PageNotFound />}
					></Route>
				</Route>
			) : (
				<>
					{loading && (
						<>
							<Route
								path="PageNotFound"
								element={<PageNotFound />}
							></Route>
							<Route path="*" element={<UnknownRoute />}></Route>
						</>
					)}
				</>
			)}
		</Routes>
	);
};

export default AppRoutes;
